import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { node } from "prop-types";
import { Text, Stack } from "@chaser/components";
import styles from "./SubscriptionTransitionContext.module.scss";

const SubscriptionTransitionContext = createContext({
  isBlocking: false,
  startTransition: () => {},
  endTransition: () => {},
});

const OVERLAY_MESSAGE = "Updating your subscription...";

const SubscriptionTransitionProvider = ({ children }) => {
  const [pendingCount, setPendingCount] = useState(0);

  const startTransition = useCallback(() => {
    setPendingCount((count) => count + 1);
  }, []);

  const endTransition = useCallback(() => {
    setPendingCount((count) => Math.max(0, count - 1));
  }, []);

  const value = useMemo(
    () => ({
      isBlocking: pendingCount > 0,
      startTransition,
      endTransition,
    }),
    [pendingCount, startTransition, endTransition],
  );

  return (
    <SubscriptionTransitionContext.Provider value={value}>
      {children}
      <div
        className={`${styles.overlay} ${pendingCount > 0 ? styles.active : styles.inactive}`}
        role="status"
        aria-live="polite"
        aria-busy={pendingCount > 0}
      >
        <Stack
          gap="medium"
          align="center"
          justify="center"
          alignItems="center"
          className={styles.overlayContent}
        >
          <div className={styles.spinner} />
          <Stack textAlign="center">
            <Text
              align="center"
              weight={600}
              style={{ color: "#ffffff", fontSize: "19px", lineHeight: 1.2 }}
            >
              {OVERLAY_MESSAGE}
            </Text>
            <Text
              align="center"
              fontColor="Mid Neutral 1"
              style={{
                color: "rgba(255, 255, 255, 0.92)",
                fontSize: "15px",
                lineHeight: 1.2,
              }}
            >
              Please wait while we refresh your account.
            </Text>
          </Stack>
        </Stack>
      </div>
    </SubscriptionTransitionContext.Provider>
  );
};

SubscriptionTransitionProvider.propTypes = {
  children: node.isRequired,
};

const useSubscriptionTransition = () =>
  useContext(SubscriptionTransitionContext);

export { SubscriptionTransitionProvider, useSubscriptionTransition };
